import { Injectable } from '@angular/core';
import { BaseSignalStore } from '@core/state/base-store';
import { ApiPaginatedResponse, NotaireTableData, NotaryOfficesState } from '@shared/models/notaire/notaire.models';

/**
 * Store for managing NotaryOffices state
 * Handles data, loading, error states, and filtering/sorting
 */
@Injectable({ providedIn: 'root' })
export class NotaryOfficesStore extends BaseSignalStore<NotaryOfficesState> {
  constructor() {
    super({
      notaires: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 20,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
      filters: {},
      sort: {}
    });
  }

  // Computed selectors
  readonly notaires = this.select(state => state.notaires);
  readonly totalCount = this.select(state => state.totalCount);
  readonly currentPage = this.select(state => state.currentPage);
  readonly pageSize = this.select(state => state.pageSize);
  readonly totalPages = this.select(state => state.totalPages);
  readonly hasPreviousPage = this.select(state => state.hasPreviousPage);
  readonly hasNextPage = this.select(state => state.hasNextPage);
  readonly filters = this.select(state => state.filters);
  readonly sort = this.select(state => state.sort);
  readonly isEmpty = this.select(state => state.notaires.length === 0);

  // Observable selectors for RxJS integration
  readonly notaires$ = this.select$(state => state.notaires);
  readonly totalCount$ = this.select$(state => state.totalCount);
  readonly currentPage$ = this.select$(state => state.currentPage);
  readonly pageSize$ = this.select$(state => state.pageSize);
  readonly totalPages$ = this.select$(state => state.totalPages);
  readonly hasPreviousPage$ = this.select$(state => state.hasPreviousPage);
  readonly hasNextPage$ = this.select$(state => state.hasNextPage);
  readonly filters$ = this.select$(state => state.filters);
  readonly sort$ = this.select$(state => state.sort);
  readonly isEmpty$ = this.select$(state => state.notaires.length === 0);

  /**
   * Sets the NotaryOffices data and pagination info
   */
  setNotaryOffices(notaires: NotaireTableData[], pagination: ApiPaginatedResponse<unknown>['pagination']): void {
    this.setState({
      notaires,
      totalCount: pagination.count,
      currentPage: pagination.pageNumber,
      pageSize: pagination.pageSize,
      totalPages: pagination.countPages,
      hasPreviousPage: pagination.hasPreviousPage,
      hasNextPage: pagination.hasNextPage
    });
  }

  /**
   * Updates the filters
   */
  setFilters(filters: Partial<NotaryOfficesState['filters']>): void {
    this.setState({
      filters: { ...this.state().filters, ...filters }
    });
  }

  /**
   * Updates the sort configuration
   */
  setSort(column: string, direction: 'asc' | 'desc'): void {
    this.setState({
      sort: { column, direction }
    });
  }

  /**
   * Updates pagination
   */
  setPagination(page: number, pageSize: number): void {
    this.setState({
      currentPage: page,
      pageSize
    });
  }

  /**
   * Clears all filters
   */
  clearFilters(): void {
    this.setState({
      filters: {}
    });
  }

  /**
   * Clears sort configuration
   */
  clearSort(): void {
    this.setState({
      sort: {}
    });
  }

  /**
   * Resets the store to initial state
   */
  override reset(): void {
    super.reset();
  }
}
